/**
 * Canonical App Store / Play Store URLs and helpers for the smart
 * `/get-the-app` redirect + mobile CTAs.
 */

export const APP_STORE_URL = 'https://apps.apple.com/us/app/nsave-global-usd-accounts/id6471736519';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.nsave.app&hl=en_GB';

/** Internal path used by CTAs and dynamic QR codes (`origin` + this path). */
export const GET_THE_APP_PATH = '/get-the-app';

/**
 * Absolute production URL encoded in static QR SVGs
 * (`public/images/shared/*-qr.svg`). Live UI generates QR from the current
 * origin instead so local/preview scans work before DNS cutover.
 */
export const GET_THE_APP_URL = 'https://www.nsave.com/get-the-app';

export type AppStorePlatform = 'ios' | 'android' | 'other';

export function platformFromUserAgent(ua: string): AppStorePlatform {
  if (/Android/i.test(ua)) return 'android';
  // iPadOS 13+ reports as Macintosh with touch — handled in isMobileClient.
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  return 'other';
}

/** Store URL for a mobile UA, or `null` when the device is not clearly mobile. */
export function storeUrlForUserAgent(ua: string): string | null {
  switch (platformFromUserAgent(ua)) {
    case 'android':
      return PLAY_STORE_URL;
    case 'ios':
      return APP_STORE_URL;
    default:
      return null;
  }
}

/**
 * Client-side: treat phones/tablets (including iPadOS) as mobile so "Get the
 * app" skips the QR modal and opens a store instead.
 */
export function isMobileClient(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (platformFromUserAgent(ua) !== 'other') return true;
  // iPadOS desktop-mode UA
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Client-side store URL for the current device (iPadOS → App Store). */
export function storeUrlForClient(): string {
  if (typeof navigator === 'undefined') return APP_STORE_URL;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return APP_STORE_URL;
  }
  return storeUrlForUserAgent(navigator.userAgent) ?? APP_STORE_URL;
}
